<script lang="ts">
	import procedureStepImage1Sm from "@assets/images/steps/1.png?webp&w=250&imagetools";
	import procedureStepImage1Lg from "@assets/images/steps/1.png?webp&w=500&imagetools";
	import procedureStepImage2Sm from "@assets/images/steps/2.png?webp&w=250&imagetools";
	import procedureStepImage2Lg from "@assets/images/steps/2.png?webp&w=500&imagetools";
	import procedureStepImage3Sm from "@assets/images/steps/3.jpg?webp&w=250&imagetools";
	import procedureStepImage3Lg from "@assets/images/steps/3.jpg?webp&w=500&imagetools";
	import calendarIcon from "@core/assets/icons/calendar.svg?raw";
	import type { Procedure } from "@core/schema/Procedure.js";
	import { getUploadedFilePath } from "@core/utils/getUploadedFilePath.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { homepageData } from "../../data/homepageData.js";
	import Button from "../common/Button.svelte";
	import CkEditorContent from "../common/CkEditorContent.svelte";
	import Formatted from "../common/Formatted.svelte";
	import Link from "../common/Link.svelte";
	import Media from "../common/Media.svelte";
	import Modal from "../common/Modal.svelte";
	import Reviews from "../common/Reviews.svelte";
	import { ButtonVariant } from "../common/getButtonClass.js";
	import Process from "../home/Process.svelte";
	import VoucherOrderForm from "../vouchers/VoucherOrderForm.svelte";

	const procedureStepImages = [
		{ sm: procedureStepImage1Sm, lg: procedureStepImage1Lg },
		{ sm: procedureStepImage2Sm, lg: procedureStepImage2Lg },
		{ sm: procedureStepImage3Sm, lg: procedureStepImage3Lg },
	];

	const { procedure, procedures }: { procedure: Procedure; procedures: Procedure[] } = $props();

	const { FILE_BUCKET_URL_MAP } = getGlobalSettings();

	function formatDuration(minutes: number): string {
		return `(${minutes} min)`;
	}

	function formatPrice(price: number): string {
		return `${price.toLocaleString("cs-CZ")} Kč`;
	}

	const procedureSteps = homepageData.procedureProcessSteps as { stepNumber: string; headline: string; text: string }[];

	const videoUrl = getUploadedFilePath(procedure.video, FILE_BUCKET_URL_MAP);

	let showVoucherModal = $state(false);
	const procedureTitle = procedure.title.replace("nl[[]]", " ");

	// const uploadedImages = procedure.images.map((img) => getUploadedFilePath(img, FILE_BUCKET_URL_MAP));
	// const allImages = uploadedImages.length > 0 ? uploadedImages : fallbackImages;
</script>

<section class="flex flex-col items-start bg-ivory-200 full-width">
	<div
		class="flex flex-col justify-center items-center px-4 sm:px-8 md:px-16 lg:px-32 pb-6 sm:pb-8 md:pb-16 gap-6 md:gap-16 w-full"
	>
		<div
			class="flex flex-col lg:flex-row items-center gap-4 sm:gap-5 md:gap-8 lg:gap-16 w-full max-w-[74rem] bg-transparent border-0 shadow-none"
		>
			<div class="flex flex-col items-start gap-2 sm:gap-3 md:gap-4 w-full lg:w-[35rem] shrink">
				<div
					class="w-full h-[14rem] sm:h-[18rem] md:h-[20rem] lg:h-[23.75rem] rounded-[2rem] sm:rounded-[3rem] md:rounded-[4rem] overflow-hidden relative"
				>
					<video
						autoplay
						loop
						muted
						playsinline
						class="w-full h-full object-cover"
						width={560}
						height={380}
						preload="metadata"
					>
						<source src={videoUrl} type="video/mp4" />
					</video>
				</div>
			</div>

			<div
				class="flex flex-col items-start pt-0 pb-4 sm:pb-6 md:pb-8 lg:pb-16 gap-4 sm:gap-5 md:gap-8 w-full lg:w-[35rem] flex-grow"
			>
				<div class="flex flex-col items-start gap-4 sm:gap-5 md:gap-6 w-full">
					<h1 class="font-heading text-lg md:text-xl lg:text-2xl font-bold text-hazelnut-500 w-full leading-[110%]">
						<Formatted text={procedure.title} />
						<br />
						<span class="inline-block">
							{formatDuration(procedure.duration)}
						</span>
					</h1>

					<p class="text-sm sm:text-base md:text-base font-medium text-midnight w-full leading-[140%]">
						<Formatted text={procedure.description} />
					</p>
				</div>

				<div class="flex flex-row flex-wrap items-start content-start gap-1 sm:gap-2 w-full">
					{#each procedure.tags as tag (tag)}
						<span
							class="flex justify-center items-center px-2 sm:px-3 py-0.5 sm:py-1 rounded-[2rem] sm:rounded-full text-[10px] sm:text-xs font-medium leading-[0.875rem] sm:leading-tight uppercase border border-[#F7CFCF] text-midnight whitespace-nowrap"
						>
							<Formatted text={tag} />
						</span>
					{/each}
				</div>

				<div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-3 w-full">
					<p class="text-lg font-bold text-midnight leading-tight">
						{formatPrice(procedure.price)}
					</p>

					<Link
						href="https://head-spa-na-pohodu.reservio.com/"
						target="_blank"
						variant={ButtonVariant.PrimaryMedium}
						iconStart={calendarIcon}
						class="w-full sm:w-auto"
					>
						Rezervovat termín
					</Link>
				</div>
			</div>
		</div>
	</div>

	{#if procedure.ckeditorContent}
		<div class="px-4 sm:px-8 md:px-16 lg:px-32 py-6 sm:py-10 md:py-16 w-full max-w-[74rem] mx-auto">
			<CkEditorContent
				html={procedure.ckeditorContent}
				lightboxId="procedure-{procedure.id}"
				class="prose max-w-none
				prose-headings:font-bold prose-headings:text-hazelnut-500 prose-headings:mb-4 prose-headings:mt-6
				prose-h2:text-xl prose-h2:md:text-2xl prose-h2:leading-tight
				prose-h3:text-lg prose-h3:md:text-xl prose-h3:leading-tight
				prose-h4:text-base prose-h4:md:text-lg prose-h4:leading-tight
				prose-p:text-sm prose-p:md:text-base prose-p:text-midnight prose-p:leading-relaxed prose-p:mb-4 prose-p:font-normal
				prose-a:text-hazelnut-600 prose-a:no-underline hover:prose-a:underline prose-a:font-normal
				prose-strong:text-midnight prose-strong:font-semibold
				prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6 prose-ul:text-midnight
				prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6 prose-ol:text-midnight
				prose-li:text-sm prose-li:md:text-base prose-li:leading-relaxed prose-li:mb-1 prose-li:font-normal
				prose-img:rounded-2xl prose-img:my-4"
			/>
		</div>
	{/if}

	<section
		class="flex flex-col items-center px-4 sm:px-8 md:px-16 lg:px-32 py-6 sm:py-12 md:py-16 gap-6 md:gap-20 w-full max-w-[90rem] mx-auto"
	>
		<h2
			class="font-heading text-lg sm:text-xl md:text-2xl lg:text-[3rem] font-bold text-center text-hazelnut-500 leading-[110%] w-full max-w-[39.5rem]"
		>
			<Formatted text="Jak probíhají procedury" />
		</h2>

		<div class="hidden lg:flex flex-row items-start justify-between w-full max-w-[74rem] gap-4">
			{#each procedureSteps as step, index (index)}
				<div class="flex flex-col items-center gap-6 flex-1">
					<div class="flex flex-col items-center gap-4">
						{#if procedureStepImages[index]}
							<Media
								sourcesets={{
									">lg": procedureStepImages[index].lg,
								}}
								source={{ src: procedureStepImages[index].sm, type: "image/webp" }}
								alt=""
								width={500}
								height={500}
								class="w-16 h-16 md:w-20 md:h-20 xl:w-24 xl:h-24 rounded-full object-cover"
							/>
						{:else}
							<div class="size-24 bg-ivory-100 rounded-full"></div>
						{/if}
						<span class="w-full text-center font-bold text-xs tracking-[0.33em] text-hazelnut-400 uppercase">
							<Formatted text={step.stepNumber} />
						</span>
					</div>

					<div class="flex flex-col items-start gap-2 w-full">
						<span class="w-full font-bold text-md text-center text-hazelnut-500 leading-[130%]">
							<Formatted text={step.headline} />
						</span>
						<p class="w-full text-p leading-[140%] font-normal text-center text-midnight">
							<Formatted text={step.text} />
						</p>
					</div>
				</div>

				{#if index < procedureSteps.length - 1}
					<div class="flex flex-col justify-center items-center w-20 h-24 shrink-0">
						<div class="w-full h-0 border-t-2 border-ivory-400"></div>
					</div>
				{/if}
			{/each}
		</div>

		<div class="flex lg:hidden flex-col items-center gap-6 sm:gap-8 w-full max-w-[74rem]">
			{#each procedureSteps as step, index (index)}
				<div class="flex flex-col items-center gap-3 sm:gap-4 w-full max-w-[22rem] px-4">
					<div class="flex flex-col items-center gap-2 sm:gap-3">
						{#if procedureStepImages[index]}
							<Media
								sourcesets={{
									">lg": procedureStepImages[index].lg,
								}}
								source={{ src: procedureStepImages[index].sm, type: "image/webp" }}
								alt=""
								width={500}
								height={500}
								class="w-16 h-16 md:w-20 md:h-20 xl:w-24 xl:h-24 rounded-full object-cover"
							/>
						{:else}
							<div class="size-24 bg-ivory-100 rounded-full"></div>
						{/if}
						<span
							class="w-full text-center font-bold text-[11px] sm:text-xs tracking-[0.33em] text-hazelnut-400 uppercase"
						>
							<Formatted text={step.stepNumber} />
						</span>
					</div>

					<div class="flex flex-col items-start gap-1.5 sm:gap-2 w-full">
						<span class="w-full font-bold text-xs sm:text-sm md:text-base text-center text-hazelnut-500 leading-[130%]">
							<Formatted text={step.headline} />
						</span>
						<p
							class="w-full text-[0.8125rem] sm:text-xs md:text-sm leading-[140%] font-normal text-center text-midnight"
						>
							<Formatted text={step.text} />
						</p>
					</div>
				</div>
			{/each}
		</div>
	</section>

	<section
		class="flex flex-col items-center px-4 sm:px-8 md:px-16 lg:px-32 py-8 sm:py-12 md:py-16 gap-4 md:gap-6 w-full bg-[#604339]/5"
	>
		<h2 class="font-heading text-base sm:text-lg md:text-xl font-bold text-hazelnut-500 leading-[110%] text-center">
			Darujte zážitek napohodu
		</h2>
		<p class="text-xs sm:text-sm md:text-base text-midnight leading-[140%] font-medium text-center max-w-[30rem]">
			Potěšte své blízké dárkovým poukazem na tuto proceduru. Voucher doručíme e-mailem nebo si ho můžete vyzvednout
			osobně.
		</p>
		<Button
			variant={ButtonVariant.SecondaryMedium}
			onclick={(): void => {
				showVoucherModal = true;
			}}
		>
			Koupit voucher
		</Button>
	</section>

	<Process {procedure} />

	<Reviews reviews={homepageData.reviews} showButton={false} />

	<!-- <section
		class="flex flex-col items-center px-4 sm:px-8 md:px-16 lg:px-24 py-6 sm:py-12 md:py-16 gap-6 md:gap-16 w-full max-w-[90rem] mx-auto"
	>
		<div class="flex flex-col items-start gap-4 sm:gap-6 w-full max-w-[78rem]">
			{#each resolvedInfoCards as card, index (index)}
				<InfoCard
					headline={card.headline}
					text={card.text}
					image={card.image}
					imageAlt={card.imageAlt}
					buttonText={card.buttonText}
					buttonHref={card.buttonHref}
				/>
			{/each}
		</div>
		<div class="flex justify-center items-center w-full">
			<Link
				href="https://head-spa-na-pohodu.reservio.com/"
				target="_blank"
				variant={ButtonVariant.PrimaryMedium}
				iconStart={calendarIcon}>rezervovat termín</Link
			>
		</div>
	</section> -->

	<div class="flex justify-center items-center w-full">
		<Link
			href="https://head-spa-na-pohodu.reservio.com/"
			target="_blank"
			variant={ButtonVariant.PrimaryMedium}
			iconStart={calendarIcon}>rezervovat termín</Link
		>
	</div>
</section>

<Modal bind:show={showVoucherModal}>
	<div class="flex flex-col gap-4 md:gap-6 w-full max-w-md">
		<div class="text-center">
			<h2 class="font-heading text-xl md:text-3xl font-bold text-hazelnut mb-1 md:mb-2">Objednávka voucheru</h2>
			<p class="text-copper-300 font-semibold text-base md:text-lg hidden md:block">{procedureTitle}</p>
		</div>
		<VoucherOrderForm
			{procedures}
			selectedProcedureTitle={procedureTitle}
			successCallback={(): void => {
				showVoucherModal = false;
			}}
		/>
	</div>
</Modal>
